import React from "react";
import { motion } from "framer-motion";
import CheckoutButton from "./CheckoutButton";
import { Headphones, Clock, Infinity as InfinityIcon } from "lucide-react";
import HERO_IMG from "../assets/hero.png";
import WAVE_IMG from "../assets/wave.png";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden pt-28 md:pt-32 pb-20 md:pb-28"
    >
      {/* Mobile-only top portrait — visible above the headline */}
      <div className="md:hidden relative h-[42vh] -mt-16 mb-6 overflow-hidden">
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-[center_20%]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-transparent to-obsidian" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
      </div>

      {/* Desktop background portrait */}
      <div className="absolute inset-0 pointer-events-none hidden md:block">
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-[70%_center] opacity-[0.7]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/65 to-obsidian/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
        <img
          src={WAVE_IMG}
          alt=""
          className="absolute -bottom-10 left-0 right-0 w-full h-1/3 object-cover opacity-20 mix-blend-screen"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-12 items-center">
        <div className="md:col-span-8 lg:col-span-7">
          <motion.p
            {...fade(0)}
            data-testid="hero-eyebrow"
            className="text-xs md:text-sm tracking-[0.35em] uppercase text-gold mb-6 md:mb-8"
          >
            Expérience audio · 4 pistes
          </motion.p>

          <motion.h1
            {...fade(0.1)}
            data-testid="hero-headline"
            className="font-display uppercase font-medium leading-[0.92] tracking-[-0.025em] text-[14vw] sm:text-[10vw] md:text-[7.5vw] lg:text-[6.5rem] xl:text-[7.5rem]"
          >
            <span className="block text-ink-50">Stop au</span>
            <span className="block text-ink-50">syndrome</span>
            <span className="block">
              <span className="text-ink-50">de l'</span>
              <span className="font-serif italic font-light text-gold normal-case tracking-tight">
                imposteur
              </span>
            </span>
          </motion.h1>

          <motion.p
            {...fade(0.25)}
            data-testid="hero-subhead"
            className="mt-8 md:mt-10 font-serif italic text-2xl md:text-3xl text-ink-100/90 lowercase tracking-wide max-w-xl"
          >
            arrête de négocier ta place.
          </motion.p>

          <motion.p
            {...fade(0.35)}
            data-testid="hero-support"
            className="mt-6 text-base md:text-lg text-ink-400 leading-relaxed max-w-xl"
          >
            Une expérience audio émotionnelle pour apaiser les doutes,
            réactiver ta légitimité intérieure et oser enfin poser un acte
            concret.
          </motion.p>

          <motion.div
            {...fade(0.5)}
            className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8"
          >
            <CheckoutButton testId="hero-cta-button" showArrow>
              Je reprends ma place
            </CheckoutButton>

            <div className="flex items-baseline gap-3">
              <span className="font-display text-3xl md:text-4xl text-ink-50">
                77 €
              </span>
            </div>
          </motion.div>

          <motion.ul
            {...fade(0.65)}
            data-testid="hero-microbenefits"
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs tracking-[0.2em] uppercase text-ink-400"
          >
            <li className="flex items-center gap-2">
              <Headphones className="w-4 h-4 text-gold" /> 100 % audio
            </li>
            <li className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gold" /> À ton rythme
            </li>
            <li className="flex items-center gap-2">
              <InfinityIcon className="w-4 h-4 text-gold" /> Accès à vie
            </li>
          </motion.ul>
        </div>

        {/* Right column — soundwave visual */}
        <motion.div
          {...fade(0.4)}
          className="md:col-span-4 lg:col-span-5 hidden md:flex justify-end items-end"
        >
          <div className="relative">
            <div className="flex items-end gap-[6px] h-48">
              {[28, 64, 40, 110, 80, 150, 96, 180, 130, 70, 100, 50, 80, 36].map(
                (h, i) => (
                  <span
                    key={i}
                    className="soundwave-bar w-[3px] bg-gold/80 rounded-full"
                    style={{
                      height: `${h}px`,
                      animationDelay: `${i * 0.12}s`,
                    }}
                  />
                ),
              )}
            </div>
            <p className="mt-6 font-serif italic text-ink-400 text-sm tracking-wide max-w-[220px]">
              une voix, une musique, une vibration — pour revenir dans ton axe.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        {...fade(0.9)}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] uppercase text-ink-400/70"
      >
        ↓ Descendre
      </motion.div>
    </section>
  );
}
